import React, { useCallback, useState } from "react";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import WorkspaceEmptyState from "assets/images/WorkspaceEmptyState.png";
import { defaultContact } from "components/Characteristics/Deal-form-drawer";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Notice } from "components/Prompts/Notice";
import { useActionHook, useIsViewerPermission, usePrompt } from "hooks";
import { IApiServiceError, useDealActions } from "store";
import pickFiles from "utils/pick-files";

import { useDealCreateContext } from "../context/DealCreateContext";
import { AiLoadingState } from "./AiLoadingState";

const ACCEPTED_TYPES = ".pdf";

export const ChoiceScreen: React.FC = () => {
  const { setField, setState, goToScreen, assetClasses, stages, templates } = useDealCreateContext();
  const { extractDealFromFileAction } = useDealActions();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();
  const [isDragging, setIsDragging] = useState(false);

  const {
    action: extractFromFile,
    loading: extracting,
    error: extractionError,
    resetError: resetExtractionError
  } = useActionHook(extractDealFromFileAction);

  const handleFileSelected = useCallback(
    async (file: File) => {
      setField("file", file);
      setField("flowPath", "file-upload");

      try {
        const result = await extractFromFile(file);

        setState((prev) => {
          const updates: Partial<typeof prev> = {};

          if (result.deal_name) updates.dealName = result.deal_name;
          if (result.currency) updates.currency = result.currency;
          if (result.geography) updates.geography = result.geography;

          // Resolve deal_stage_id → stageId
          if (result.deal_stage_id) {
            const matchedStage = stages.find((s) => s.id === result.deal_stage_id);
            if (matchedStage) updates.stageId = matchedStage.id;
          }

          // Resolve asset_class_id → IAssetClass object
          if (result.asset_class_id) {
            const matched = assetClasses.find((ac) => ac.id === result.asset_class_id);
            if (matched) {
              updates.assetClass = matched;
              // Resolve sub_asset_class_id within the matched parent
              if (result.sub_asset_class_id) {
                const sub = matched.sub_asset_class.find((s) => s.id === result.sub_asset_class_id);
                if (sub) updates.subAssetClass = sub;
              }
            }
          }

          // Map characteristics by ID
          if (result.characteristics?.length) {
            const charData = result.characteristics
              .filter((c) => c.value)
              .map((c) => ({
                localId: crypto.randomUUID(),
                characteristic_id: c.characteristic_id,
                value: c.value,
                currency: null,
                percentage: 100,
                is_default: true
              }));
            if (charData.length) updates.characteristicsData = charData;
          }

          // Map contact fields — AI extracts a single contact, seed the contacts list with it.
          if (result.contact) {
            updates.contacts = [
              {
                ...defaultContact,
                id: crypto.randomUUID(),
                first_name: result.contact.first_name || "",
                last_name: result.contact.last_name || "",
                title: result.contact.title || "",
                firm: result.contact.firm || "",
                phone_number: result.contact.phone_number || "",
                email: result.contact.email || "",
                address: result.contact.address || "",
                role: null
              }
            ];
          }

          // Preselect template
          if (result.template_id) {
            const matchedTemplate = templates.find((t) => t.id === result.template_id);
            if (matchedTemplate) updates.templateId = matchedTemplate.id;
          }

          return { ...prev, ...updates };
        });

        goToScreen("basic-form");
      } catch {
        // Error is captured by useActionHook's error state
      }
    },
    [extractFromFile, setField, setState, goToScreen, assetClasses, stages, templates]
  );

  const handleBrowse = useCallback(async () => {
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    const fileList = await pickFiles({ accept: ACCEPTED_TYPES });
    if (!fileList?.length) return;
    handleFileSelected(fileList[0]);
  }, [handleFileSelected, isAnalystViewerPermission, openPrompt]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (isAnalystViewerPermission) {
        openPrompt({ Component: Notice, props: { type: "trial" } });
        return;
      }
      const dropped = e.dataTransfer.files?.[0];
      if (!dropped || !dropped.name.toLowerCase().endsWith(".pdf")) return;
      handleFileSelected(dropped);
    },
    [handleFileSelected, isAnalystViewerPermission, openPrompt]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleManual = () => {
    setField("flowPath", "manual");
    goToScreen("basic-form");
  };

  const handleExtractionBack = useCallback(() => {
    resetExtractionError();
    setField("file", null);
    handleManual();
  }, [setField, resetExtractionError]);

  const extractionErrorMessage = extractionError
    ? Object.values((extractionError as IApiServiceError)?.errors || {})
        .flat()
        .join(". ") ||
      (extractionError as IApiServiceError)?.message ||
      "Failed to extract data from file"
    : null;

  if (extracting || extractionErrorMessage) {
    return <AiLoadingState isLoading={extracting} error={extractionErrorMessage} onBack={handleExtractionBack} />;
  }

  return (
    <Stack sx={{ padding: "32px", gap: "20px", flexGrow: 1 }}>
      {/* Illustration */}
      <Stack alignItems="center">
        <Box sx={{ height: "118px", width: "188px", overflow: "hidden" }}>
          <Box
            component="img"
            src={WorkspaceEmptyState}
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
          />
        </Box>
      </Stack>
      {/* Select Action header */}
      <Stack sx={{ gap: "3px" }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#071f35", lineHeight: "20px" }}>
          Select Action
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#496682", lineHeight: "16px" }}>
          There are several ways to create a deal, choose the most appropriate way.
        </Typography>
      </Stack>

      {/* Actions container */}
      <Stack sx={{ gap: "20px" }}>
        {/* Drag & drop file zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            bgcolor: "#f6f8fc",
            border: "1px dashed",
            borderColor: isDragging ? "#26a6ae" : "#dbe8f5",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            height: "173px",
            cursor: "pointer",
            transition: "border-color 0.2s",
            ...(isDragging && { bgcolor: "#eef6f7" })
          }}
          onClick={handleBrowse}
        >
          <Stack alignItems="center">
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35", lineHeight: "24px" }}>
              Drag & drop file
            </Typography>
            <Stack direction="row" alignItems="center" gap="2px">
              <Typography component="span" sx={{ fontSize: "14px", color: "#496682", lineHeight: "16px" }}>
                Upload deal PDF to extract information
              </Typography>
              <MaterialTooltip
                title="To create a new deal, you can use any financial document from which the necessary data will be extracted, or download a structured template, fill it out, and then upload it back into the system."
                isShow
                placement="right"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "#496682", cursor: "pointer" }} />
              </MaterialTooltip>
            </Stack>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: "16px !important" }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleBrowse();
            }}
            sx={{
              textTransform: "none",
              borderRadius: "32px",
              fontSize: "12px",
              lineHeight: "16px",
              color: "#071f35",
              borderColor: "#7b92a9",
              bgcolor: "white",
              px: "8px",
              py: "4px",
              maxWidth: "240px",
              "&:hover": { borderColor: "#496682", bgcolor: "#f6f8fc" }
            }}
          >
            Select File
          </Button>
        </Box>

        {/* Or divider */}
        <Stack alignItems="center">
          <Typography sx={{ fontSize: "14px", color: "#496682", lineHeight: "16px" }}>Or</Typography>
        </Stack>

        {/* Manual Entry card */}
        <Stack
          direction="row"
          onClick={handleManual}
          sx={{
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
            padding: "10px",
            gap: "10px",
            height: "64px",
            cursor: "pointer",
            "&:hover": { boxShadow: "0px 2px 8px 0px rgba(18,18,23,0.16), 0px 1px 4px 0px rgba(18,18,23,0.1)" }
          }}
        >
          <Stack
            sx={{
              bgcolor: "#ecf1f8",
              borderRadius: "8px",
              width: 44,
              height: 44,
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <EditNoteOutlinedIcon sx={{ fontSize: 20, color: "#071f35", opacity: 0.8 }} />
          </Stack>
          <Stack sx={{ gap: "1px", justifyContent: "center", minWidth: 0 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#071f35", lineHeight: "20px" }}>
              Manual Entry
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#496682", lineHeight: "16px" }}>
              Start from scratch by using the form
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
